import { genreList } from '../../../resources/base/models';
import { EnumCommercialVehicleTransform } from '../../../resources/base/models/enums';
import { VehicleType } from '../../../resources/base/models/vehicleTypes';
import { getMilesCatalogVehicleIdFromVehicle, pickOptionsValue } from '../../../services/common/miscUtils';
import { VehicleConfigData } from '../../../types/vehicleConfig';
import {
  calculateRegistrationCosts,
  getTotalRegistrationCost,
} from '../../RegistrationCostModal/registrationCostsUtils';
import { formatDate, getVehicleType } from '../../utils';

export const calculateTotalAccessoriesPrice = (options: Array<QuoteSchema.Schemas.VehicleOptionGetQuote>): number => {
  // 400001 - Accessory: with VAT 20%, 400002 - Accessory: with VAT 5,5%
  const filteredOptions = options.filter((listItem) => listItem.prKey === '400001' || listItem.prKey === '400002');
  const accessoriesTotal =
    filteredOptions.reduce((total, listItem) => {
      return Number(listItem.optionPriceAmount?.grossAmount ?? 0) + total;
    }, 0) ?? 0;

  return accessoriesTotal;
};

export const calculateTotalOptionsPrice = (options: Array<QuoteSchema.Schemas.VehicleOptionGetQuote>): number => {
  const optionsTotalAmount: number =
    options.reduce((total, listItem) => {
      // Excluded options
      // 400001 - Accessory: with VAT 20%, 400002 - Accessory: with VAT 5,5%
      if (listItem.prKey === '400001' || listItem.prKey === '400002') {
        return total;
      }

      return Number(listItem.optionPriceAmount?.grossAmount ?? 0) + total;
    }, 0) ?? 0;

  return optionsTotalAmount;
};

export const getBtoOptionsPrice = (options: Array<QuoteSchema.Schemas.VehicleOptionGetQuote>): number => {
  return options.find((option) => option.prKey === '400003')?.optionPriceAmount?.grossAmount || 0;
};

const getTransformationSoc = (quote: QuoteSchema.Schemas.QuoteResponse): boolean | undefined => {
  return quote?.vehicleData?.model?.options?.find((option) => option.prKey === '250001')?.isSelected;
};

const getResolution = (quote: QuoteSchema.Schemas.QuoteResponse): boolean | undefined => {
  const quoteResolution = quote?.financialProduct?.offers?.find(
    (offer) => !!offer.calculation?.commercialVehicleTransform
  );

  return (
    (quoteResolution?.calculation?.commercialVehicleTransform as EnumCommercialVehicleTransform) ===
    EnumCommercialVehicleTransform.restore
  );
};

const getIsRimsSelected = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  rimOptions: VehicleIntegration.Components.Schemas.ModelOptions[]
): boolean => {
  return (
    quote.vehicleData?.model?.options?.some((option) =>
      rimOptions.find((rim) => rim.prTypeId === option.prKey && option.isSelected)
    ) ?? false
  );
};

const getRims = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  rimOptions: VehicleIntegration.Components.Schemas.ModelOptions[]
): string => {
  return (
    quote.vehicleData?.model?.options?.find((option) =>
      rimOptions.find((rim) => rim.prTypeId === option.prKey && option.isSelected)
    )?.prKey ?? ''
  );
};

export const calculateTotalDeliveryCost = (
  options: Array<QuoteSchema.Schemas.DeliveryCostComponentCalculateSalesQuoteResponse>
): number => {
  const priceWithTax0 =
    options.find((component) => component.deliveryCostAmount?.taxCode === 'NoTaxInland')?.deliveryCostAmount
      ?.grossAmount ?? 0;

  const priceWithTax20 =
    options.find((component) => component.deliveryCostAmount?.taxCode === 'FranceInputVATInland20')?.deliveryCostAmount
      ?.grossAmount ?? 0;

  return priceWithTax0 + priceWithTax20;
};

export const getPrefillOrDefaultValues = (
  vehicleType: VehicleType,
  quoteData?: QuoteSchema.Schemas.QuoteResponse,
  rimOptions?: VehicleIntegration.Components.Schemas.ModelOptions[]
): VehicleConfigData => {
  const defaultValues: VehicleConfigData = {
    brand: '',
    model: '',
    modelGroup: '',
    engine: '',
    modelName: '',
    modelDescription: '',
    catalogPrice: undefined,
    catalogPriceBto: undefined,
    fiscalPower: undefined,
    regDate: '',
    regCost: undefined,
    transformationSoc: undefined,
    batteryPrice: 0,
    co2emision: '',
    currMileage: undefined,
    estDelivery: '',
    department: undefined,
    genreList: pickOptionsValue(genreList, 'DVP'),
    resolution: undefined,
    deliveryCost: 0,
    vehicleType,
    isDemo: false,
    licensePlate: '',
    vin: '',
    isRimSelected: false,
    rims: '',
    accessoires: 0,
    options: 0,
    totalRegCost: undefined,
    vehicleMilesId: undefined,
    dinHp: undefined,
  };

  if (!quoteData) {
    return defaultValues;
  }
  const { vehicleData, financialProduct } = quoteData;
  const registrationCosts = calculateRegistrationCosts(quoteData);
  const totalRegistrationCosts = getTotalRegistrationCost(registrationCosts);

  if (!vehicleData?.milesVehicleType) {
    throw new Error('Missing Vehicle Type');
  }

  const retailPriceAmount = vehicleData?.retailPriceAmount?.grossAmount ?? 0;
  const totalAccessoriesPrice =
    calculateTotalAccessoriesPrice(quoteData.vehicleData?.model?.options ?? []) ?? defaultValues.accessoires;
  const btoOptionsPrice = getBtoOptionsPrice(quoteData.vehicleData?.model?.options ?? []);
  // Subtracting total accessories and BTO options from the retail price ensures the "pure" base catalog value is displayed.
  const newCarCatalogPrice = retailPriceAmount - totalAccessoriesPrice - btoOptionsPrice;

  const catalogPriceBto = newCarCatalogPrice;

  const mappedValues: VehicleConfigData = {
    ...defaultValues,
    brand: vehicleData?.model?.manufacturer ? vehicleData.model.manufacturer : defaultValues.brand,
    modelGroup: vehicleData?.model?.name ?? defaultValues.modelGroup,
    modelName: vehicleData?.model?.description ?? defaultValues.modelName,
    modelDescription: vehicleData?.model?.description,
    fiscalPower: vehicleData?.fiscalPower ?? defaultValues.fiscalPower,
    model: vehicleData?.model?.year?.toString() ?? defaultValues.model,
    engine: vehicleData?.model?.engine?.fuelTypeLabel ?? defaultValues.engine,
    regDate: vehicleData?.initialRegistrationDate
      ? formatDate(vehicleData?.initialRegistrationDate)
      : defaultValues.regDate,
    regCost: Boolean(financialProduct?.offers?.[0].calculation?.includingRegistration),
    currMileage: vehicleData?.odometer ?? defaultValues.currMileage,
    estDelivery: financialProduct?.contracts?.[0].deliveryData?.deliveryDate
      ? formatDate(financialProduct?.contracts?.[0].deliveryData?.deliveryDate)
      : defaultValues.estDelivery,
    vin: vehicleData?.vin ?? defaultValues.vin,
    licensePlate: vehicleData?.licensePlate ?? defaultValues.licensePlate,
    co2emision: vehicleData?.model?.economics?.[0].emissions?.[0].combined?.value ?? defaultValues.co2emision,
    catalogPrice: vehicleData?.milesDealerVehiclePrice?.grossAmount || vehicleData?.retailPriceAmount?.grossAmount,
    catalogPriceBto,
    transformationSoc: getTransformationSoc(quoteData),
    department: financialProduct?.offers?.[0]?.calculation?.registrationDepartment ?? defaultValues.department,
    resolution: getResolution(quoteData),
    deliveryCost:
      calculateTotalDeliveryCost(
        quoteData?.financialProduct?.offers?.[0]?.deliveryOptions?.[0].deliveryCostComponents ?? []
      ) ?? defaultValues.deliveryCost,
    vehicleType: vehicleType || getVehicleType(quoteData),
    isDemo: vehicleData?.milesVehicleType?.toUpperCase() === 'DEMO' ? true : defaultValues.isDemo,
    accessoires: totalAccessoriesPrice,
    options: calculateTotalOptionsPrice(quoteData.vehicleData?.model?.options ?? []),
    freeOptionsBTO: btoOptionsPrice,
    totalRegCost: totalRegistrationCosts,
    batteryPrice: quoteData.vehicleData?.model?.battery?.batteryPrice?.grossAmount || defaultValues.batteryPrice,
    vehicleMilesId:
      getMilesCatalogVehicleIdFromVehicle(
        vehicleData as { externalVehicleIdentifiers?: Array<VehicleIntegration.Components.Schemas.ExternalIdentifier> }
      ) ?? undefined,
    dinHp: vehicleData?.model?.engine?.powerDetails?.powerValues?.find((pw) => pw.unit === 'HORSEPOWER')?.value,
    isRimSelected: rimOptions ? getIsRimsSelected(quoteData, rimOptions) : defaultValues.isRimSelected,
    rims: rimOptions ? getRims(quoteData, rimOptions) : defaultValues.rims,
  };
  return mappedValues;
};
